import { Integration, IntegrationSyncTypes, IntegrationTypesWithFaker } from 'src/shared/types/integration.types';
import { Tag } from 'src/shared/types/model.types';

// List of integration types that have setup schemas
// Keep in sync with src/shared/schemas/integrations/schemas.ts
const INTEGRATION_TYPES_WITH_SETUP: readonly string[] = [
  'outlook',
  'salesforce',
  'gmail',
  's3',
  'nylasV3_imap',
  'nylasV3_icloud',
  'demo',
  'ftp',
  'powerbiV2',
  'looker',
  'sac',
  'qlik',
  'metabase',
  'tableau',
  'sharepoint',
  'saps4hana',
];

export const integrationTypesWithFaker: readonly IntegrationTypesWithFaker[] = [
  'nylas',
  'nylasV3',
  'outlook',
  'gmail',
  'zendesk',
  'imap',
  'sapservicecloudv2',
  'salesforce',
  'slack',
  'hubspot',
  'powerbiV2',
  'sac',
  'qlik',
  'tableau',
  'looker',
  'sharepoint',
  'faker',
  'ftp',
  'demo',
  'saps4hana',
  'servicenow',
  'sapecc',
  'inforM3',
] as const;

export const syncIntegrationTypes: readonly IntegrationTypesWithFaker[] = [
  'nylas',
  'nylasV3',
  'outlook',
  'gmail',
  'zendesk',
  'salesforce',
  'hubspot',
  'sapservicecloudv2',
  'freshdesk',
  'servicenow',
  'ftp',
  's3',
  'demo',
  'faker',
  'saps4hana',
];

export function getIntegrationImage(type: IntegrationTypesWithFaker | string) {
  switch (type) {
    case 'adsoluterp':
      return '/static/images/integrations/adsolut-icon.png';
    case 'outlook':
      return '/static/images/integrations/outlook-icon.png';
    case 'demo':
    case 'salesforce':
      return '/static/images/integrations/salesforce-icon.png';
    case 'gmail':
      return '/static/images/integrations/gmail-icon.png';
    case 'zendesk':
      return '/static/images/integrations/zendesk-icon.png';
    case 'slack':
      return '/static/images/integrations/slack-icon.png';
    case 'powerbiV2':
      return '/static/images/integrations/powerbi-icon.png';
    case 'looker':
      return '/static/images/integrations/looker-icon.webp';
    case 'sapservicecloud':
    case 'sapservicecloudv2':
    case 'sap_business_one':
    case 'sapecc':
    case 'saps4hana':
    case 'sac':
      return '/static/images/integrations/sap-icon.png';
    case 'tableau':
      return '/static/images/integrations/tableau-icon.svg';
    case 'qlik':
      return '/static/images/integrations/qlik-icon.png';
    case 'hubspot':
      return '/static/images/integrations/hubspot-icon.png';
    case 'sharepoint':
      return '/static/images/integrations/sharepoint-icon.png';
    case 'ftp':
      return '/static/images/integrations/ftp-icon.jpg';
    case 'metabase':
      return '/static/images/integrations/metabase-icon.png';
    case 'sendcloud':
      return '/static/images/integrations/sendcloud-icon.png';
    case 's3':
      return '/static/images/integrations/s3-icon.png';
    case 'api':
      return '/static/images/integrations/file-icon.png';
    case 'freshdesk':
      return '/static/images/integrations/freshdesk-icon.png';
    case 'nylasV3_yahoo':
      return '/static/images/integrations/yahoo-icon.png';
    case 'nylasV3_icloud':
      return '/static/images/integrations/icloud-icon.png';
    case 'servicenow':
      return '/static/images/integrations/servicenow-icon.png';
    case 'inforM3':
      return '/static/images/integrations/infor-icon.png';
    case 'nylas':
    case 'nylasV3_imap':
    case 'nylasV3':
    case 'imap':
    default:
      return '/static/images/integrations/mail.svg';
  }
}

export function getIntegrationDisplayName(type: IntegrationTypesWithFaker) {
  switch (type) {
    case 'nylas':
      return 'Email';
    case 'nylasV3':
      return 'Other Email V2';
    case 'nylasV3_imap':
      return 'IMAP';
    case 'nylasV3_yahoo':
      return 'Yahoo! Mail';
    case 'nylasV3_icloud':
      return 'iCloud';
    case 'imap':
      return 'IMAP';
    case 'outlook':
      return 'Outlook';
    case 'salesforce':
      return 'Salesforce';
    case 'gmail':
      return 'Gmail';
    case 'zendesk':
      return 'Zendesk';
    case 'slack':
      return 'Slack';
    case 'powerbiV2':
      return 'Power BI';
    case 'sac':
      return 'Sap Analytics Cloud';
    case 'sapservicecloudv2':
      return 'SAP Service Cloud';
    case 'looker':
      return 'Looker';
    case 'tableau':
      return 'Tableau';
    case 'qlik':
      return 'Qlik';
    case 'hubspot':
      return 'HubSpot';
    case 'sharepoint':
      return 'SharePoint';
    case 'ftp':
      return 'FTP';
    case 'sendcloud':
      return 'Sendcloud';
    case 's3':
      return 'AWS S3';
    case 'api':
      return 'File Upload';
    case 'saps4hana':
      return 'SAP S/4HANA';
    case 'freshdesk':
      return 'Freshdesk';
    case 'adsoluterp':
      return 'Adsolut ERP';
    case 'demo':
      return 'Demo';
    case 'faker':
      return 'Faker';
    case 'inforM3':
      return 'Infor M3';
    case 'metabase':
      return 'Metabase';
    case 'servicenow':
      return 'ServiceNow';
    case 'sapecc':
      return 'SAP ECC';
    default:
      return type;
  }
}

export function getIntegrationType(integration: Integration | undefined): IntegrationTypesWithFaker {
  if (!integration) {
    return 'outlook';
  }

  if (integration.type === 'demo') {
    return integration.settings?.fakerType ?? integration.type;
  }
  if (integration.type.includes('nylasV3')) {
    if (integration.settings?.provider === 'imap') {
      return 'nylasV3_imap';
    }
    if (integration.settings?.provider === 'yahoo') {
      return 'nylasV3_yahoo';
    }
    if (integration.settings?.provider === 'icloud') {
      return 'nylasV3_icloud';
    }
    return 'nylasV3';
  }
  return integration.type === 'faker' ? (integration.settings?.fakerType ?? 'outlook') : integration.type;
}

export function getHasIntegrationSetup(type: IntegrationTypesWithFaker): boolean {
  return INTEGRATION_TYPES_WITH_SETUP.includes(type);
}

export function isInbox(type: IntegrationTypesWithFaker): type is IntegrationSyncTypes {
  const inboxTypes: IntegrationTypesWithFaker[] = [
    'nylas',
    'nylasV3',
    's3',
    'api',
    'outlook',
    'gmail',
    'zendesk',
    'imap',
    'salesforce',
    'faker',
    'sapservicecloudv2',
    'hubspot',
    'ftp',
    'freshdesk',
    'adsoluterp',
    'demo',
    'servicenow',
    'saps4hana',
    'sap_business_one',
  ];
  return inboxTypes.includes(type);
}

export const inboxStage: Record<Integration['syncStage'][0]['stage'], string> = {
  BACKFILL_ANALYTICS: 'Backfilling messages, this might take a while...',
  CONFIGURATION: 'Finish the inbox setup',
  BACKFILL_MODELS: 'Updating models',
};

export const syncStagePriorityOrder = ['BACKFILL_MODELS', 'BACKFILL_ANALYTICS', 'CONFIGURATION'];

export function getHighestPrioritySyncStage(syncStage: Integration['syncStage']) {
  return syncStage?.length > 0
    ? syncStage.sort((a, b) => syncStagePriorityOrder.indexOf(a.stage) - syncStagePriorityOrder.indexOf(b.stage))[0]
    : undefined;
}

export function getIsFileIntegration(type: IntegrationTypesWithFaker | undefined) {
  if (!type) {
    return false;
  }
  const fileIntegrations: IntegrationTypesWithFaker[] = ['s3', 'ftp', 'sharepoint', 'demo', 'api'];
  return fileIntegrations.includes(type);
}

export function hasResolutionTime(type: IntegrationTypesWithFaker): boolean {
  const resolutionTimeIntegrations: IntegrationTypesWithFaker[] = ['zendesk', 'salesforce'];
  return resolutionTimeIntegrations.includes(type);
}

export function hasResponseTime(type: IntegrationTypesWithFaker, featureFlag?: boolean): boolean {
  return !hasResolutionTime(type) || (!!featureFlag && type === 'salesforce');
}

export function getIsChildAccount(
  integration: Integration<'outlook' | 'gmail' | 'salesforce' | 'adsoluterp'> | undefined
) {
  if (!integration) {
    return false;
  }
  if (integration.parentIntegrationId) {
    return true;
  }
  if (integration.type === 'adsoluterp' || integration.type === 'gmail' || integration.type === 'salesforce') {
    return !!integration.settings?.parentAccount;
  }
  return !!integration.settings?.parentId;
}

export function getIsAutoTag(tag: Tag) {
  return tag.type === 'auto' && tag.status === 'SUGGESTED';
}

export function getIsServiceAccount(integration: Integration<'outlook' | 'gmail'> | undefined): boolean {
  if (!integration) return false;

  return !!integration.settings?.adminAccount;
}

export function getIntegrationById(id: string | undefined, integrations: Integration[]) {
  if (!id) {
    return undefined;
  }
  return integrations.find((iteratedIntegration) => iteratedIntegration.id === id);
}

export function getParentAccount(
  integration: Integration<'adsoluterp' | 'outlook' | 'salesforce'> | undefined,
  integrations: Integration<'adsoluterp' | 'outlook' | 'salesforce'>[]
) {
  if (!integration) {
    return undefined;
  }

  const parentId = integration.parentIntegrationId ?? integration.settings?.parentAccount;
  return getIntegrationById(parentId, integrations);
}
